import { POSITION_LABELS, type PositionValue } from "@/lib/positions"

interface PositionZone {
  value: PositionValue
  label: string
  /** X position as percentage of field width */
  x: number
  /** Y position as percentage of field height */
  y: number
}

/**
 * Position zones placed on a top-down football field.
 * The field is oriented vertically: goalkeeper at bottom, attackers at top.
 * Coordinates are percentages (0-100) of the field dimensions.
 */
const POSITION_ZONES: PositionZone[] = [
  { value: "portero", label: "POR", x: 50, y: 92 },
  { value: "defensa_central", label: "DFC", x: 50, y: 78 },
  { value: "lateral_derecho", label: "LD", x: 82, y: 75 },
  { value: "lateral_izquierdo", label: "LI", x: 18, y: 75 },
  { value: "mediocampista_defensivo", label: "MCD", x: 50, y: 60 },
  { value: "mediocampista_central", label: "MC", x: 50, y: 47 },
  { value: "mediocampista_ofensivo", label: "MCO", x: 50, y: 35 },
  { value: "extremo_derecho", label: "ED", x: 82, y: 30 },
  { value: "extremo_izquierdo", label: "EI", x: 18, y: 30 },
  { value: "delantero_centro", label: "DC", x: 50, y: 15 },
  { value: "segundo_delantero", label: "SD", x: 35, y: 22 },
]

interface PositionPickerProps {
  value: string | null
  onChange: (value: PositionValue | null) => void
  secondaryValue?: string | null
  onSecondaryChange?: (value: PositionValue | null) => void
}

export default function PositionPicker({
  value,
  onChange,
  secondaryValue,
  onSecondaryChange,
}: PositionPickerProps) {
  const hasSecondaryMode = onSecondaryChange !== undefined

  function handleClick(positionValue: PositionValue) {
    // If clicking the current primary, deselect it
    if (value === positionValue) {
      onChange(null)
      return
    }

    // If clicking the current secondary, deselect it
    if (hasSecondaryMode && secondaryValue === positionValue) {
      onSecondaryChange(null)
      return
    }

    // If no primary is set, or not in secondary mode, set primary
    if (!value || !hasSecondaryMode) {
      onChange(positionValue)
      return
    }

    // Primary is set and we're in secondary mode: set secondary
    onSecondaryChange(positionValue)
  }

  function getButtonClasses(positionValue: PositionValue): string {
    const base =
      "min-w-[44px] min-h-[44px] absolute flex items-center justify-center rounded-full text-xs font-bold transition-all duration-200 cursor-pointer select-none"

    if (value === positionValue) {
      return `${base} bg-krown-accent text-krown-800 ring-2 ring-krown-accent/50 scale-110`
    }

    if (secondaryValue === positionValue) {
      return `${base} bg-crown text-krown-800 ring-2 ring-crown/50 scale-110`
    }

    return `${base} bg-white/20 text-white hover:bg-white/40 hover:scale-105`
  }

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Football field */}
      <div className="relative w-full max-w-[320px]">
        <svg
          viewBox="0 0 340 500"
          className="h-auto w-full"
          aria-label="Campo de fútbol"
        >
          {/* Field background */}
          <rect x="0" y="0" width="340" height="500" rx="8" fill="#1a7a3a" />

          {/* Field stripes (darker green alternating) */}
          <rect x="0" y="0" width="340" height="62.5" rx="8" fill="#1a7a3a" />
          <rect x="0" y="62.5" width="340" height="62.5" fill="#167032" />
          <rect x="0" y="125" width="340" height="62.5" fill="#1a7a3a" />
          <rect x="0" y="187.5" width="340" height="62.5" fill="#167032" />
          <rect x="0" y="250" width="340" height="62.5" fill="#1a7a3a" />
          <rect x="0" y="312.5" width="340" height="62.5" fill="#167032" />
          <rect x="0" y="375" width="340" height="62.5" fill="#1a7a3a" />
          <rect
            x="0"
            y="437.5"
            width="340"
            height="62.5"
            rx="8"
            fill="#167032"
          />

          {/* Field outline */}
          <rect
            x="15"
            y="15"
            width="310"
            height="470"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />

          {/* Center line */}
          <line
            x1="15"
            y1="250"
            x2="325"
            y2="250"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />

          {/* Center circle */}
          <circle
            cx="170"
            cy="250"
            r="50"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />

          {/* Center dot */}
          <circle cx="170" cy="250" r="4" fill="white" fillOpacity="0.6" />

          {/* Top penalty area */}
          <rect
            x="70"
            y="15"
            width="200"
            height="80"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />

          {/* Top goal area */}
          <rect
            x="110"
            y="15"
            width="120"
            height="35"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />

          {/* Top penalty arc */}
          <path
            d="M 130 95 Q 170 115 210 95"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />

          {/* Top penalty dot */}
          <circle cx="170" cy="75" r="3" fill="white" fillOpacity="0.6" />

          {/* Bottom penalty area */}
          <rect
            x="70"
            y="405"
            width="200"
            height="80"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />

          {/* Bottom goal area */}
          <rect
            x="110"
            y="450"
            width="120"
            height="35"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />

          {/* Bottom penalty arc */}
          <path
            d="M 130 405 Q 170 385 210 405"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />

          {/* Bottom penalty dot */}
          <circle cx="170" cy="425" r="3" fill="white" fillOpacity="0.6" />

          {/* Corner arcs */}
          <path
            d="M 15 25 Q 25 15 35 15"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />
          <path
            d="M 305 15 Q 315 15 325 25"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />
          <path
            d="M 15 475 Q 25 485 35 485"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />
          <path
            d="M 305 485 Q 315 485 325 475"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeOpacity="0.6"
          />
        </svg>

        {/* Position buttons overlaid on field */}
        {POSITION_ZONES.map((zone) => (
          <button
            key={zone.value}
            type="button"
            aria-label={POSITION_LABELS[zone.value]}
            className={getButtonClasses(zone.value)}
            style={{
              left: `${zone.x}%`,
              top: `${zone.y}%`,
              transform: "translate(-50%, -50%)",
            }}
            onClick={() => handleClick(zone.value)}
          >
            {zone.label}
          </button>
        ))}
      </div>

      {/* Position label(s) below field */}
      <div className="flex items-center gap-3 text-sm">
        {value ? (
          <span className="flex items-center gap-1.5">
            <span className="bg-krown-accent inline-block h-3 w-3 rounded-full" />
            {POSITION_LABELS[value as PositionValue]}
          </span>
        ) : (
          <span className="text-muted-foreground">Seleccioná una posición</span>
        )}

        {hasSecondaryMode && secondaryValue ? (
          <span className="flex items-center gap-1.5">
            <span className="bg-crown inline-block h-3 w-3 rounded-full" />
            {POSITION_LABELS[secondaryValue as PositionValue]}
          </span>
        ) : null}
      </div>
    </div>
  )
}
